import { Component, type ErrorInfo, type ReactNode } from "react";

interface OcErrorBoundaryProps {
  children: ReactNode;
}

interface OcErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level error boundary for the self-service portal. Catches render-time
 * exceptions anywhere below it and shows a branded, neutral fallback so the
 * user never sees a blank white page. Styling mirrors the inline OcErrorView
 * fallback used in App.tsx.
 */
export class OcErrorBoundary extends Component<OcErrorBoundaryProps, OcErrorBoundaryState> {
  constructor(props: OcErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): OcErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the failure in the console for diagnostics; there is no telemetry
    // sink on the public portal.
    console.error("OcErrorBoundary caught an error", error, info.componentStack);
  }

  private handleReload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-full w-full bg-slate-50 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-sm ring-1 ring-slate-200 p-8 text-center">
            <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
            <p className="mt-2 text-sm text-slate-600">
              An unexpected error occurred. Please reload the page and try again.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className="mt-6 inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-700"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default OcErrorBoundary;
